import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Box, Center, HoverCard, Image, Portal, Spinner, Text, VStack } from '@chakra-ui/react';
import { gql } from '@src/lib/gql';
import React, { ReactNode, useState } from 'react';

/** All RAG source-type keys currently emitted by startups3 adapters. */
export type RagSourceTypeKey = 'cms-article' | 'cms-podcast-episode' | 'cms-video' | 'app-playbook' | 'app-feature';
export type RagContentKind = 'article' | 'podcast' | 'video' | 'playbook' | 'feature';
export type RagContentRef = {
  kind: RagContentKind;
  slug: string;
};
const STARTUPS_HOST_RE = /(^|\.)startups\.com$/i;

/** Map sco-citation source-type keys to content kinds used by the hover preview. */
export function kindFromSourceType(sourceType?: string | null): RagContentKind | null {
  switch (sourceType) {
    case 'cms-article':
      return 'article';
    case 'cms-podcast-episode':
      return 'podcast';
    case 'cms-video':
      return 'video';
    case 'app-playbook':
      return 'playbook';
    case 'app-feature':
      return 'feature';
    default:
      return null;
  }
}
export function typeLabelForSourceType(sourceType?: string | null): string {
  switch (sourceType) {
    case 'cms-article':
      return 'Article';
    case 'cms-podcast-episode':
      return 'Podcast';
    case 'cms-video':
      return 'Video';
    case 'app-playbook':
      return 'Playbook';
    case 'app-feature':
      return 'Product';
    default:
      return sourceType ? sourceType.replace(/^(cms|app)-/, '').replace(/-/g, ' ') : 'Source';
  }
}
function typeLabelForKind(kind: RagContentKind): string {
  switch (kind) {
    case 'article':
      return 'Article';
    case 'podcast':
      return 'Podcast';
    case 'video':
      return 'Video';
    case 'playbook':
      return 'Playbook';
    case 'feature':
      return 'Product';
    default:
      return 'Source';
  }
}

/**
 * Parse a citation / markdown href into a content ref we can look up.
 * Supports absolute startups.com URLs and relative site paths used by RAG adapters.
 */
export function parseRagContentRef(href?: string | null, sourceType?: string | null): RagContentRef | null {
  if (!href) {
    // No URL — still allow feature/playbook previews keyed only by source-type when slug unknown.
    return null;
  }
  let pathname = href;
  try {
    if (/^https?:\/\//i.test(href)) {
      const url = new URL(href);
      if (!STARTUPS_HOST_RE.test(url.hostname)) {
        return null;
      }
      pathname = url.pathname;
    } else if (href.startsWith('//')) {
      const url = new URL(`https:${href}`);
      if (!STARTUPS_HOST_RE.test(url.hostname)) {
        return null;
      }
      pathname = url.pathname;
    } else if (!href.startsWith('/')) {
      return null;
    }
  } catch {
    return null;
  }

  // Strip trailing slash and query/hash leftovers
  const path = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  const articleMatch = path.match(/^\/articles\/([^/]+)$/);
  if (articleMatch) {
    return {
      kind: 'article',
      slug: decodeURIComponent(articleMatch[1])
    };
  }
  const podcastMatch = path.match(/^\/podcast\/([^/]+)$/);
  if (podcastMatch) {
    return {
      kind: 'podcast',
      slug: decodeURIComponent(podcastMatch[1])
    };
  }

  // /videos/{listSlug}/{videoSlug} or /videos/{videoSlug}
  const videoNested = path.match(/^\/videos\/[^/]+\/([^/]+)$/);
  if (videoNested) {
    return {
      kind: 'video',
      slug: decodeURIComponent(videoNested[1])
    };
  }
  const videoSingle = path.match(/^\/videos\/([^/]+)$/);
  if (videoSingle) {
    return {
      kind: 'video',
      slug: decodeURIComponent(videoSingle[1])
    };
  }

  // /playbooks/{slug} or /playbooks/{slug}/{section}
  const playbookMatch = path.match(/^\/playbooks\/([^/]+)/);
  if (playbookMatch) {
    return {
      kind: 'playbook',
      slug: decodeURIComponent(playbookMatch[1])
    };
  }

  // App features use product paths (/members/matching, /tools, /advisors, …).
  // Prefer explicit source-type; otherwise treat unknown app paths as features when tagged.
  const kindFromType = kindFromSourceType(sourceType);
  if (kindFromType === 'feature') {
    return {
      kind: 'feature',
      slug: path
    };
  }
  if (kindFromType === 'playbook') {
    const segments = path.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last) {
      return {
        kind: 'playbook',
        slug: decodeURIComponent(last)
      };
    }
  }

  // Fallback: source-type known but path didn't match standard patterns — still try last segment
  if (kindFromType === 'article' || kindFromType === 'podcast' || kindFromType === 'video') {
    const segments = path.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last) {
      return {
        kind: kindFromType,
        slug: decodeURIComponent(last)
      };
    }
  }
  return null;
}
type PreviewData = {
  title?: string | null;
  imageUrl?: string | null;
  snippet?: string | null;
  typeLabel: string;
};
function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const v of values) {
    if (v && String(v).trim()) {
      return String(v).trim();
    }
  }
  return null;
}
const ARTICLE_PREVIEW_QUERY = gql`
  query RagSourceArticlePreview($slug: String!) {
    article(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      teaser
      seoDescription
      synopsis
      tldr
      featuredImage {
        id
        url
      }
    }
  }
`;
const PODCAST_PREVIEW_QUERY = gql`
  query RagSourcePodcastPreview($slug: String!) {
    podcastEpisode(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      description
      seoDescription
      posterImage {
        id
        url
      }
      seoImage {
        id
        url
      }
    }
  }
`;
const VIDEO_PREVIEW_QUERY = gql`
  query RagSourceVideoPreview($slug: String!) {
    video(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      shortDescription
      longDescription
      externalImageUrl
      featuredImage {
        id
        url
      }
    }
  }
`;
const PLAYBOOK_PREVIEW_QUERY = gql`
  query RagSourcePlaybookPreview($slug: String!) {
    scoPlaybook(slug: $slug) {
      id
      title
      teaser
      tldr
      illustration
      seoDescription
    }
  }
`;
function useRagSourcePreview(ref, enabled) {
  const $ = _c(43);
  const t0 = ref?.slug;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      slug: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !enabled || !ref || ref.kind !== "article";
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      variables: t1,
      skip: t2,
      ssr: false
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const articleQuery = useQuery(ARTICLE_PREVIEW_QUERY, t3);
  const t4 = ref?.slug;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      slug: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !enabled || !ref || ref.kind !== "podcast";
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = {
      variables: t5,
      skip: t6,
      ssr: false
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const podcastQuery = useQuery(PODCAST_PREVIEW_QUERY, t7);
  const t8 = ref?.slug;
  let t9;
  if ($[10] !== t8) {
    t9 = {
      slug: t8
    };
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = !enabled || !ref || ref.kind !== "video";
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = {
      variables: t9,
      skip: t10,
      ssr: false
    };
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const videoQuery = useQuery(VIDEO_PREVIEW_QUERY, t11);
  const t12 = ref?.slug;
  let t13;
  if ($[15] !== t12) {
    t13 = {
      slug: t12
    };
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = !enabled || !ref || ref.kind !== "playbook";
  let t15;
  if ($[17] !== t13 || $[18] !== t14) {
    t15 = {
      variables: t13,
      skip: t14,
      ssr: false
    };
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const playbookQuery = useQuery(PLAYBOOK_PREVIEW_QUERY, t15);
  if (!ref) {
    let t16;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        loading: false,
        data: null
      };
      $[20] = t16;
    } else {
      t16 = $[20];
    }
    return t16;
  }
  if (ref.kind === "article") {
    const article = articleQuery.data?.article;
    let t16;
    if ($[21] !== article) {
      t16 = article ? {
        title: article.title,
        imageUrl: article.featuredImage?.url,
        snippet: firstNonEmpty(article.teaser, article.seoDescription, article.synopsis, article.tldr),
        typeLabel: "Article"
      } : null;
      $[21] = article;
      $[22] = t16;
    } else {
      t16 = $[22];
    }
    let t17;
    if ($[23] !== articleQuery.loading || $[24] !== t16) {
      t17 = {
        loading: articleQuery.loading,
        data: t16
      };
      $[23] = articleQuery.loading;
      $[24] = t16;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    return t17;
  }
  if (ref.kind === "podcast") {
    const episode = podcastQuery.data?.podcastEpisode;
    let t16;
    if ($[26] !== episode) {
      t16 = episode ? {
        title: episode.title,
        imageUrl: episode.posterImage?.url || episode.seoImage?.url,
        snippet: firstNonEmpty(episode.description, episode.seoDescription),
        typeLabel: "Podcast"
      } : null;
      $[26] = episode;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    let t17;
    if ($[28] !== podcastQuery.loading || $[29] !== t16) {
      t17 = {
        loading: podcastQuery.loading,
        data: t16
      };
      $[28] = podcastQuery.loading;
      $[29] = t16;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    return t17;
  }
  if (ref.kind === "video") {
    const video = videoQuery.data?.video;
    let t16;
    if ($[31] !== video) {
      t16 = video ? {
        title: video.title,
        imageUrl: video.featuredImage?.url || video.externalImageUrl,
        snippet: firstNonEmpty(video.shortDescription, video.longDescription),
        typeLabel: "Video"
      } : null;
      $[31] = video;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    let t17;
    if ($[33] !== t16 || $[34] !== videoQuery.loading) {
      t17 = {
        loading: videoQuery.loading,
        data: t16
      };
      $[33] = t16;
      $[34] = videoQuery.loading;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    return t17;
  }
  if (ref.kind === "playbook") {
    const playbook = playbookQuery.data?.scoPlaybook;
    let t16;
    if ($[36] !== playbook) {
      t16 = playbook ? {
        title: playbook.title,
        imageUrl: playbook.illustration || null,
        snippet: firstNonEmpty(playbook.teaser, playbook.tldr, playbook.seoDescription),
        typeLabel: "Playbook"
      } : null;
      $[36] = playbook;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== playbookQuery.loading || $[39] !== t16) {
      t17 = {
        loading: playbookQuery.loading,
        data: t16
      };
      $[38] = playbookQuery.loading;
      $[39] = t16;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    return t17;
  }
  if (ref.kind === "feature") {
    let t16;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        loading: false,
        data: {
          title: null,
          imageUrl: null,
          snippet: null,
          typeLabel: "Product"
        }
      };
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    return t16;
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      loading: false,
      data: null
    };
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
type RagSourcePreviewBodyProps = {
  contentRef: RagContentRef;
  /** Fallback title when CMS lookup is still loading or fails */
  fallbackTitle?: string;
  fallbackSnippet?: string;
  fallbackImageUrl?: string;
};
const RagSourcePreviewBody = props => {
  const $ = _c(22);
  const {
    loading,
    data
  } = useRagSourcePreview(props.contentRef, true);
  const title = data?.title || props.fallbackTitle || "Source";
  const snippet = data?.snippet || props.fallbackSnippet;
  const imageUrl = data?.imageUrl || props.fallbackImageUrl;
  let t0;
  if ($[0] !== data?.typeLabel || $[1] !== props.contentRef) {
    t0 = data?.typeLabel || typeLabelForKind(props.contentRef.kind);
    $[0] = data?.typeLabel;
    $[1] = props.contentRef;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const typeLabel = t0;
  let t1;
  if ($[3] !== imageUrl || $[4] !== loading || $[5] !== title) {
    t1 = imageUrl ? <Box bg="bg.muted" borderRadius="md" overflow="hidden"><Image alt={title} display="block" maxH="140px" objectFit="cover" src={imageUrl} w="full" /></Box> : loading ? <Center bg="bg.muted" borderRadius="md" minH="80px"><Spinner size="sm" /></Center> : null;
    $[3] = imageUrl;
    $[4] = loading;
    $[5] = title;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== typeLabel) {
    t2 = <Badge colorPalette="blue" size="sm" variant="subtle" w="fit-content">{typeLabel}</Badge>;
    $[7] = typeLabel;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== title) {
    t3 = <Text fontSize="sm" fontWeight="semibold" lineClamp={2}>{title}</Text>;
    $[9] = title;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== data?.title || $[12] !== loading || $[13] !== snippet) {
    t4 = snippet ? <Text color="fg.muted" fontSize="xs" lineClamp={3}>{snippet}</Text> : loading && !data?.title ? <Center py={1}><Spinner size="xs" /></Center> : null;
    $[11] = data?.title;
    $[12] = loading;
    $[13] = snippet;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <VStack align="stretch" gap={1}>{t2}{t3}{t4}</VStack>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t1 || $[20] !== t5) {
    t6 = <VStack align="stretch" gap={2} maxW="280px">{t1}{t5}</VStack>;
    $[19] = t1;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};
export type RagSourceHoverCardProps = {
  href?: string | null;
  sourceType?: string | null;
  fallbackTitle?: string;
  fallbackSnippet?: string;
  fallbackImageUrl?: string;
  children: ReactNode;
  /** When false, render children without hover card */
  enabled?: boolean;
};

/**
 * Chakra V3 hover-card wrapper for linked RAG sources.
 * Looks up featured image + title + teaser/description by content type when available;
 * falls back to citation attributes for app-feature and when CMS is unavailable.
 */
export const RagSourceHoverCard = props => {
  const $ = _c(26);
  let t0;
  if ($[0] !== props.href || $[1] !== props.sourceType) {
    t0 = parseRagContentRef(props.href, props.sourceType);
    $[0] = props.href;
    $[1] = props.sourceType;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const contentRef = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[3] !== contentRef || $[4] !== props.fallbackTitle || $[5] !== props.href || $[6] !== props.sourceType) {
    t1 = contentRef || (kindFromSourceType(props.sourceType) ? {
      kind: kindFromSourceType(props.sourceType),
      slug: props.href || props.fallbackTitle || "source"
    } as RagContentRef : null);
    $[3] = contentRef;
    $[4] = props.fallbackTitle;
    $[5] = props.href;
    $[6] = props.sourceType;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const effectiveRef = t1;
  if (props.enabled === false || !effectiveRef) {
    let t2;
    if ($[8] !== props.children) {
      t2 = <>{props.children}</>;
      $[8] = props.children;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      placement: "top",
      gutter: 8
    };
    t3 = details => setOpen(details.open);
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props.children) {
    t4 = <HoverCard.Trigger asChild={true}>{props.children}</HoverCard.Trigger>;
    $[12] = props.children;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== effectiveRef || $[15] !== open || $[16] !== props.fallbackImageUrl || $[17] !== props.fallbackSnippet || $[18] !== props.fallbackTitle) {
    t5 = open ? <RagSourcePreviewBody contentRef={effectiveRef} fallbackImageUrl={props.fallbackImageUrl} fallbackSnippet={props.fallbackSnippet} fallbackTitle={props.fallbackTitle} /> : null;
    $[14] = effectiveRef;
    $[15] = open;
    $[16] = props.fallbackImageUrl;
    $[17] = props.fallbackSnippet;
    $[18] = props.fallbackTitle;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t5) {
    t6 = <Portal><HoverCard.Positioner><HoverCard.Content borderColor="border" borderRadius="xl" borderWidth="1px" boxShadow="lg" maxW="300px" p={3}>{t5}</HoverCard.Content></HoverCard.Positioner></Portal>;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== open || $[23] !== t4 || $[24] !== t6) {
    t7 = <HoverCard.Root closeDelay={150} open={open} openDelay={250} positioning={t2} onOpenChange={t3}>{t4}{t6}</HoverCard.Root>;
    $[22] = open;
    $[23] = t4;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
export default RagSourceHoverCard;